import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '80px')};
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 16px;
`;

const BrandBadge = styled.div`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  box-shadow: 0 4px 6px rgba(16, 185, 129, 0.2);
`;

const BrandText = styled.span`
  margin-left: 12px;
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 4px;
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  background: ${(p) => (p.$active ? 'linear-gradient(90deg, #10b981, #059669)' : 'transparent')};
  text-decoration: none;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${(p) => (p.$active ? 'linear-gradient(90deg, #10b981, #059669)' : '#f1f5f9')};
  }
`;

const NavDropdown = styled.div`
  margin-bottom: 4px;
`;

const NavDropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  background: ${(p) => (p.$active ? 'linear-gradient(90deg, #10b981, #059669)' : 'transparent')};
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${(p) => (p.$active ? 'linear-gradient(90deg, #10b981, #059669)' : '#f1f5f9')};
  }
`;

const NavDropdownContent = styled.div`
  max-height: ${(p) => (p.$open ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-left: ${(p) => (p.$sidebarOpen ? '32px' : '0')};
`;

const SubNavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 2px;
  color: ${(p) => (p.$active ? '#065f46' : '#475569')};
  background: ${(p) => (p.$active ? '#ecfdf5' : 'transparent')};
  text-decoration: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid ${(p) => (p.$active ? '#059669' : 'transparent')};
  
  &:hover {
    background: ${(p) => (p.$active ? '#d1fae5' : '#f8fafc')};
  }
`;

const Footer = styled.div`
  border-top: 1px solid #e2e8f0;
  padding: 16px;
  color: #64748b;
  font-size: 12px;
  text-align: center;
`;

const IconBase = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  />
);

const DashboardIcon = () => (
  <IconBase>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="4" rx="1" />
    <rect x="14" y="10" width="7" height="11" rx="1" />
    <rect x="3" y="13" width="7" height="8" rx="1" />
  </IconBase>
);

const InquiryIcon = () => (
  <IconBase>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </IconBase>
);

const TargetsIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2" />
    <path d="M21 12h-2" />
    <path d="M12 21v-2" />
    <path d="M5 12H3" />
  </IconBase>
);

const BranchIcon = () => (
  <IconBase>
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </IconBase>
);

const ChevronDownIcon = () => (
  <IconBase>
    <polyline points="6 9 12 15 18 9" />
  </IconBase>
);

export default function AdminSalesSidebar({ isOpen, onToggle, currentSection }) {
  const studentInquiriesMenu = {
    id: 'studentInquiries',
    label: 'Student Inquiries',
    icon: InquiryIcon,
    children: [
      { id: 'inquiries', label: 'Inquiries', href: '/admin-sales/inquiries' },
    ]
  };

  const isStudentInquiriesActive = studentInquiriesMenu.children.some(child => child.id === currentSection);

  const teamTargetsMenu = {
    id: 'teamTargets',
    label: 'Team Targets',
    icon: TargetsIcon,
    children: [
      { id: 'courseCode', label: 'Course Code', href: '/admin-sales/team-targets/course-code' },
      { id: 'staffCourseMapping', label: 'Coordinator Target', href: '/admin-sales/team-targets/coordinator-target' },
      { id: 'targetsSummary', label: 'Summary', href: '/admin-sales/team-targets/summary' },
    ],
  };

  const isTeamTargetsActive = teamTargetsMenu.children.some(child => child.id === currentSection);

  const branchTargetsMenu = {
    id: 'branchTargets',
    label: 'Branch Target',
    icon: BranchIcon,
    children: [
      { id: 'branchCourseCategory', label: 'Course Category', href: '/admin-sales/branch-targets/course-category' },
      { id: 'branchAnnualTarget', label: 'Annual Target', href: '/admin-sales/branch-targets/annual-target' },
      { id: 'branchMonthlyTarget', label: 'Monthly Target', href: '/admin-sales/branch-targets/monthly-target' },
      { id: 'branchNewIncome', label: 'New Income', href: '/admin-sales/branch-targets/new-income' },
    ],
  };

  const isBranchTargetsActive = branchTargetsMenu.children.some(child => child.id === currentSection);

  const [dropdownOpen, setDropdownOpen] = useState({
    studentInquiries: isStudentInquiriesActive,
    teamTargets: isTeamTargetsActive,
    branchTargets: isBranchTargetsActive,
  });

  const toggleDropdown = (key) => {
    setDropdownOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    setDropdownOpen(prev => ({
      studentInquiries: isStudentInquiriesActive ? true : prev.studentInquiries,
      teamTargets: isTeamTargetsActive ? true : prev.teamTargets,
      branchTargets: isBranchTargetsActive ? true : prev.branchTargets,
    }));
  }, [isStudentInquiriesActive, isTeamTargetsActive, isBranchTargetsActive]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon, href: '/admin-sales/dashboard' },
  ];

  return (
    <Aside $open={isOpen}>
      <TopBar>
        <BrandBadge>AS</BrandBadge>
        {isOpen && <BrandText>Admin Sales</BrandText>}
      </TopBar>
      <Nav>
        {/* Dashboard */}
        {menuItems.slice(0, 1).map((item) => {
          const Icon = item.icon;
          const isActive = currentSection === item.id;
          return (
            <NavLink key={item.id} href={item.href} $active={isActive}>
              <Icon />
              {isOpen && <span>{item.label}</span>}
            </NavLink>
          );
        })}

        {/* Branch Target Dropdown */}
        <NavDropdown>
          <NavDropdownHeader 
            onClick={() => toggleDropdown('branchTargets')}
            $active={isBranchTargetsActive}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <branchTargetsMenu.icon />
              {isOpen && <span>{branchTargetsMenu.label}</span>}
            </div>
            {isOpen && <ChevronDownIcon />}
          </NavDropdownHeader>
          {isOpen && (
            <NavDropdownContent $open={dropdownOpen.branchTargets} $sidebarOpen={isOpen}>
              {branchTargetsMenu.children.map((child) => (
                <SubNavLink 
                  key={child.id} 
                  href={child.href} 
                  $active={currentSection === child.id}
                >
                  <span>{child.label}</span>
                </SubNavLink>
              ))}
            </NavDropdownContent>
          )}
        </NavDropdown>

        {/* Student Inquiries Dropdown */}
        <NavDropdown>
          <NavDropdownHeader 
            onClick={() => toggleDropdown('studentInquiries')}
            $active={isStudentInquiriesActive}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <studentInquiriesMenu.icon />
              {isOpen && <span>{studentInquiriesMenu.label}</span>}
            </div>
            {isOpen && <ChevronDownIcon />}
          </NavDropdownHeader>
          {isOpen && (
            <NavDropdownContent $open={dropdownOpen.studentInquiries} $sidebarOpen={isOpen}>
              {studentInquiriesMenu.children.map((child) => (
                <SubNavLink 
                  key={child.id} 
                  href={child.href} 
                  $active={currentSection === child.id}
                >
                  <span>{child.label}</span>
                </SubNavLink>
              ))}
            </NavDropdownContent>
          )}
        </NavDropdown>

        {/* Team Targets Dropdown */}
        <NavDropdown>
          <NavDropdownHeader 
            onClick={() => toggleDropdown('teamTargets')}
            $active={isTeamTargetsActive}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <teamTargetsMenu.icon />
              {isOpen && <span>{teamTargetsMenu.label}</span>}
            </div>
            {isOpen && <ChevronDownIcon />}
          </NavDropdownHeader>
          {isOpen && (
            <NavDropdownContent $open={dropdownOpen.teamTargets} $sidebarOpen={isOpen}>
              {teamTargetsMenu.children.map((child) => (
                <SubNavLink 
                  key={child.id} 
                  href={child.href} 
                  $active={currentSection === child.id}
                >
                  <span>{child.label}</span>
                </SubNavLink>
              ))}
            </NavDropdownContent>
          )}
        </NavDropdown>


      </Nav>
      {isOpen && (
        <Footer>
          © 2026 Kaaryaalaya
          <br />
          Sales Admin Portal
        </Footer>
      )}
    </Aside>
  );
}
